import useAppTheme from './useAppTheme';

import {css, SerializedStyles} from '@emotion/react';

// Add more content styles here first
export const CONTENT_STYLES = [
  'accent',
  'negative',
  'neutral',
  'neutralSubdued',
  'white',
] as const;
export type ContentStyleKey = (typeof CONTENT_STYLES)[number];
export type ContentStyles = Record<ContentStyleKey, SerializedStyles>;

type ContentVariants = {
  active: string;
  defaultColor: string;
  focus: string;
  hover: string;
  disabled?: string;
};

// Add more background styles here first
export const BACKGROUND_STYLES = [
  'accent',
  'white',
  'neutral',
  'neutralSubdued',
  'neutralExtraSubdued',
  'strong',
  'gradient',
  'subAccent2',
] as const;
export type BackgroundStyleKey = (typeof BACKGROUND_STYLES)[number];
export type BackgroundStyles = Record<BackgroundStyleKey, SerializedStyles>;

type BackgroundVariants = {
  active: string;
  defaultColor: string;
  disabled: string;
  focus: string;
  hover: string;
};

export const BORDER_STYLES = [
  'neutral',
  'light',
  'accent',
  'subAccent',
  'negative',
] as const;
export type BorderStyleKey = (typeof BORDER_STYLES)[number];
export type BorderStyles = Record<BorderStyleKey, SerializedStyles>;

type BorderVariants = {
  active: string;
  defaultColor: string;
  focus: string;
  hover: string;
  disabled?: string;
};

export const DIVIDER_STYLES = ['light'] as const;
export type DividerStyleKey = (typeof DIVIDER_STYLES)[number];
export type DividerStyles = Record<DividerStyleKey, SerializedStyles>;

type DividerVariants = {
  defaultColor: string;
};

type Pallet = {
  background: Record<
    BackgroundStyleKey,
    Partial<Record<keyof BackgroundVariants, string>>
  >;
  border: Record<BorderStyleKey, Partial<Record<keyof BorderVariants, string>>>;
  content: Record<
    ContentStyleKey,
    Partial<Record<keyof ContentVariants, string>>
  >;
  divider: Record<
    DividerStyleKey,
    Partial<Record<keyof DividerVariants, string>>
  >;
};

type UseColor = {
  background: BackgroundStyles;
  border: BorderStyles;
  content: ContentStyles;
  pallet: Pallet;
};

/**
 * A hook that provides a set of color styles for content, background, and border elements,
 * along with a predefined color palette. The styles include default, hover, active, focus,
 * and disabled states for various color variants.
 *
 * @returns {UseColor} An object containing the following properties:
 * @property {Pallet} pallet - The predefined color palette used for styling.
 * @property {ContentStyles} content - Styles for content elements.
 * @property {BackgroundStyles} background - Styles for background elements
 * @property {BorderStyles} border - Styles for border elements.
 * @property {DividerStyles} divider - Styles for divider elements.
 */
const useColor = () => {
  const {colors} = useAppTheme();

  const pallet = {
    content: {
      neutral: {
        defaultColor: colors['brown-900'],
        disabled: colors['brown-400'],
      },
      neutralSubdued: {defaultColor: colors['brown-600']},
      accent: {defaultColor: colors['pink-800']},
      negative: {defaultColor: colors['red-800']},
      white: {defaultColor: colors.white},
    },
    background: {
      neutral: {
        defaultColor: colors['brown-700'],
        disabled: colors['brown-200'],
      },
      strong: {defaultColor: colors['brown-800']},
      neutralSubdued: {defaultColor: colors['brown-600']},
      neutralExtraSubdued: {defaultColor: colors['brown-100']},
      white: {defaultColor: colors.white},
      accent: {defaultColor: colors['pink-800']},
      subAccent2: {defaultColor: colors.yellow},
      gradient: {defaultColor: colors.gradient},
    },
    border: {
      neutral: {defaultColor: colors['brown-800']},
      light: {
        defaultColor: colors['brown-300'],
        hover: colors['brown-400'],
        active: colors['brown-500'],
        focus: colors['brown-400'],
      },
      accent: {defaultColor: colors['pink-400']},
      subAccent: {defaultColor: colors['green-400']},
      negative: {defaultColor: colors['red-800']},
    },
    divider: {
      light: {defaultColor: colors['brown-200']},
    },
  } as const satisfies Pallet;

  const contentVariantFactory = ({
    active,
    defaultColor,
    focus,
    hover,
  }: ContentVariants) => {
    return css({
      color: defaultColor,
      '&:hover': {color: hover},
      '&:active': {color: active},
      '&:focus-visible': {color: focus},
      '&:disabled': {color: pallet.content.neutral.disabled},
    });
  };

  const backgroundVariantFactory = ({
    active,
    defaultColor,
    disabled,
    focus,
    hover,
  }: BackgroundVariants) =>
    css({
      backgroundColor: defaultColor,
      '&:hover': {backgroundColor: hover},
      '&:active': {backgroundColor: active},
      '&:focus-visible': {backgroundColor: focus},
      '&:disabled': {backgroundColor: disabled},
    });

  const borderVariantFactory = ({
    active,
    defaultColor,
    focus,
    hover,
  }: BorderVariants) => {
    return css({
      borderColor: defaultColor,
      '&:hover': {borderColor: hover},
      '&:active': {borderColor: active},
      '&:focus-visible': {borderColor: focus},
      '&:disabled': {borderColor: colors['brown-200']},
    });
  };

  // Content
  const content = {
    neutral: contentVariantFactory({
      defaultColor: pallet.content.neutral.defaultColor,
      hover: colors['brown-1000'],
      active: colors['brown-1000'],
      focus: colors['brown-1000'],
    }),
    neutralSubdued: contentVariantFactory({
      defaultColor: pallet.content.neutralSubdued.defaultColor,
      hover: colors['brown-700'],
      active: colors['brown-700'],
      focus: colors['brown-700'],
    }),
    accent: contentVariantFactory({
      defaultColor: pallet.content.accent.defaultColor,
      hover: colors['pink-900'],
      active: colors['pink-1000'],
      focus: colors['pink-900'],
    }),
    negative: contentVariantFactory({
      defaultColor: pallet.content.negative.defaultColor,
      hover: colors['red-900'],
      active: colors['red-1000'],
      focus: colors['red-900'],
    }),
    /** Not implemented */
    white: css({}),
  } as const satisfies ContentStyles;

  // Background
  const background = {
    neutral: backgroundVariantFactory({
      defaultColor: pallet.background.neutral.defaultColor,
      hover: colors['brown-900'],
      active: colors['brown-1000'],
      focus: colors['brown-900'],
      disabled: pallet.background.neutral.disabled,
    }),
    strong: backgroundVariantFactory({
      defaultColor: pallet.background.strong.defaultColor,
      hover: colors['brown-900'],
      active: colors['brown-1000'],
      focus: colors['brown-900'],
      disabled: colors['brown-200'],
    }),
    neutralSubdued: backgroundVariantFactory({
      defaultColor: pallet.background.neutralSubdued.defaultColor,
      hover: colors['brown-400'],
      active: colors['brown-500'],
      focus: colors['brown-400'],
      disabled: colors['brown-200'],
    }),
    neutralExtraSubdued: backgroundVariantFactory({
      defaultColor: pallet.background.neutralExtraSubdued.defaultColor,
      hover: colors['brown-300'],
      active: colors['brown-100'],
      focus: colors['brown-100'],
      disabled: colors['brown-100'],
    }),
    white: backgroundVariantFactory({
      defaultColor: pallet.background.white.defaultColor,
      hover: colors['brown-200'],
      active: colors['brown-300'],
      focus: colors['brown-200'],
      disabled: colors.white,
    }),
    accent: backgroundVariantFactory({
      defaultColor: pallet.background.accent.defaultColor,
      hover: colors['pink-900'],
      active: colors['pink-1000'],
      focus: colors['pink-900'],
      disabled: colors['brown-200'],
    }),
    gradient: css({
      background: pallet.background.gradient.defaultColor,
    }),
    /** Not implemented */
    subAccent2: css({}),
  } as const satisfies BackgroundStyles;

  // Border
  const border = {
    neutral: borderVariantFactory({
      defaultColor: pallet.border.neutral.defaultColor,
      hover: colors['brown-900'],
      active: colors['brown-1000'],
      focus: colors['brown-900'],
    }),
    light: borderVariantFactory({
      defaultColor: pallet.border.light.defaultColor,
      hover: pallet.border.light.hover,
      active: pallet.border.light.active,
      focus: pallet.border.light.focus,
    }),
    /** Not implemented */
    accent: css({}),
    /** Not implemented */
    subAccent: css({}),
    /** Not implemented */
    negative: css({}),
  } as const satisfies BorderStyles;

  return {
    pallet,
    content,
    background,
    border,
  } as const satisfies UseColor;
};

export default useColor;
